/**
 * animeManager
 * -----------------
 * AnimeManager is a Class that creates an instance of an Anime timeline and
 * provides helper methods for updating and controlling it.
 */
import { __spreadArrays } from "tslib";
import * as animejs from 'animejs';
import communicateToSite from 'shared/utils/communicateToSite';
import { dispatchGlobalEvent } from 'shared/utils';
import { findStartEndPoint, showHideElement, } from './animeManagerUtils';
var AnimeManager = /** @class */ (function () {
    function AnimeManager(config) {
        var _this = this;
        if (config === void 0) { config = {}; }
        this.timelineLoopCount = 0;
        this.isTimelineStarted = false;
        this.defaultConfig = {
            autoplay: false,
        };
        this.config = {};
        this.renderers = [];
        /**
         * Every time timeline reset, it will completely create a new instance of animejs timeline
         */
        this.reset = function (config) {
            _this.timeline = animejs.timeline(config || _this.config || _this.defaultConfig);
            _this.timeline.update = function (tl) {
                _this.render();
                // round current time to the multiple of ten
                var roundedTime = Math.round(tl.currentTime / 10) * 10;
                _this.triggerHideShowOnStartEnd(roundedTime);
                // broadcast event for Adapt global code
                dispatchGlobalEvent('timelineProgress', {
                    time: roundedTime,
                });
                // Trigger onStart and onLoopStart from here...
                _this.triggerOnStart();
                _this.triggerOnLoopStart();
            };
            _this.timeline.complete = function () {
                _this.timelineLoopCount = 0;
                dispatchGlobalEvent('timelineCompleted');
            };
            _this.removeListeners();
            _this.listenToEvents();
            return _this;
        };
        this.add = function (animeParams) {
            animeParams.forEach(function (param) { return _this.timeline.add(param); });
            // re-collect time point data & animeParams
            _this.timePointData = findStartEndPoint(_this.timeline);
            _this.animeParams = animeParams;
            return _this;
        };
        /**
         * Every time timeline updates, timeline needs to be reset to have a new timeline instance
         */
        this.update = function (animeParams, config) {
            _this.reset(config)
                .add(animeParams)
                .seek(0);
            return _this;
        };
        this.play = function () {
            if (_this.timeline.duration === 0) {
                return _this;
            }
            if (_this.timeline.currentTime === _this.timeline.duration) {
                _this.restart();
            }
            else {
                _this.isTimelineStarted = true;
                _this.timeline.play();
            }
            return _this;
        };
        this.pause = function () {
            _this.timeline.pause();
            return _this;
        };
        this.seek = function (point) {
            _this.timeline.pause();
            _this.timeline.seek(point);
            return _this;
        };
        this.restart = function () { return _this.timeline.restart(); };
        this.duration = function () { return _this.timeline.duration; };
        this.addRender = function (name, render) {
            _this.renderers = _this.renderers.concat({ name: name, render: render });
            return _this;
        };
        this.removeRender = function (name) {
            var idx = _this.renderers.findIndex(function (renderer) { return renderer.name === name; });
            if (idx !== -1) {
                _this.renderers = __spreadArrays(_this.renderers.slice(0, idx), _this.renderers.slice(idx + 1));
            }
            return _this;
        };
        this.render = function () {
            _this.renderers.forEach(function (renderer) { return renderer.render(); });
            return _this;
        };
        this.isAnimating = function () {
            if (!_this.timeline) {
                return false;
            }
            return _this.timeline.remaining !== 0 && !_this.timeline.paused;
        };
        // For those animation start or end as invisible (opacity: 0) such as scenes,..
        // Only show them when they reach the starting point or within the showing range on timeline
        // SO, the next scene below can be clicked-through
        this.triggerHideShowOnStartEnd = function (currentTime) {
            _this.timePointData && showHideElement(_this.timePointData, currentTime);
        };
        this.handleSeekEvent = function (e) {
            _this.seek(e.detail.time);
        };
        this.triggerInitialStart = function (eventName) {
            if (_this.timeline.currentTime === 0 && _this.isTimelineStarted) {
                dispatchGlobalEvent(eventName);
            }
        };
        this.config = config;
        this.reset();
    }
    AnimeManager.prototype.listenToEvents = function () {
        var _this = this;
        document.addEventListener('timelineStop', this.pause);
        document.addEventListener('timelinePlay', this.play);
        document.addEventListener('timelineSeek', this.handleSeekEvent);
        communicateToSite(function (data) {
            data.visible === false ? _this.pause() : _this.play();
        });
    };
    AnimeManager.prototype.removeListeners = function () {
        document.removeEventListener('timelineStop', this.pause);
        document.removeEventListener('timelinePlay', this.play);
        document.removeEventListener('timelineSeek', this.handleSeekEvent);
    };
    AnimeManager.prototype.triggerOnStart = function () {
        this.triggerInitialStart('timelineStart');
    };
    AnimeManager.prototype.triggerOnLoopStart = function () {
        // initial start...
        this.triggerInitialStart('timelineLoopStart');
        // start of each loop...
        if (this.timeline.currentTime === this.timeline.duration &&
            this.isTimelineStarted) {
            this.timelineLoopCount++;
            if (this.timeline.loop !== this.timelineLoopCount) {
                dispatchGlobalEvent('timelineLoopStart');
            }
        }
    };
    return AnimeManager;
}());
export { AnimeManager };
var anime = new AnimeManager();
export default anime;
